'use client';

import { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import { FilterPill } from '@/components';
import { MONO_FONT, VISUALLY_HIDDEN } from '@/theme';
import PostCard from './PostCard';

// Normaliza para búsqueda: minúsculas + sin acentos.
const norm = (s) =>
  s
    .toLowerCase()
    .replace(/[áàä]/g, 'a')
    .replace(/[éèë]/g, 'e')
    .replace(/[íìï]/g, 'i')
    .replace(/[óòö]/g, 'o')
    .replace(/[úùü]/g, 'u')
    .replace(/[ñ]/g, 'n');

// Enlaces a secciones hermanas (Rankings, Tips, el blog) junto a los filtros.
const SECTION_LINK = {
  px: '10px',
  py: '5px',
  fontFamily: MONO_FONT,
  fontSize: '10.5px',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  bgcolor: 'primary.dark',
  color: 'common.white',
  borderRadius: '4px',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  transition: 'background-color 0.15s',
  '&:hover': { bgcolor: 'primary.darker' },
};

/* Feed con buscador y filtros, compartido por /blog y /actualidad. `cards` llega del
   servidor (buildCards.js); cada tarjeta trae su `group`, que es la clave de filtro.
   `filters` = [{ key, label }] sin "Todo" (se agrega solo). `sideLinks` = [{ href, label }]. */
export default function ContentFeed({ cards, filters, srTitle, searchLabel, emptyText, sideLinks = [] }) {
  const [group, setGroup] = useState('todos');
  const [query, setQuery] = useState('');
  const q = norm(query.trim());

  const pills = useMemo(
    () => [
      { key: 'todos', label: 'Todo', count: cards.length },
      ...filters.map((f) => ({ ...f, count: cards.filter((c) => c.group === f.key).length })),
    ],
    [cards, filters]
  );

  const filtered = useMemo(
    () =>
      cards.filter((c) => {
        const okGroup = group === 'todos' || c.group === group;
        const okQuery = !q || norm(`${c.title} ${c.excerpt} ${c.topic}`).includes(q);
        return okGroup && okQuery;
      }),
    [cards, group, q]
  );

  return (
    <>
      {/* h2 solo para lectores de pantalla: da jerarquía a los h3 de las tarjetas (h1 → h2 → h3) */}
      <Box component="h2" sx={VISUALLY_HIDDEN}>
        {srTitle}
      </Box>
      <Box sx={{ mb: '12px' }}>
        <Box
          component="input"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`${searchLabel}…`}
          aria-label={searchLabel}
          sx={{
            width: '100%',
            px: '14px',
            py: '10px',
            fontFamily: 'inherit',
            fontSize: '14px',
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: '6px',
            outline: 'none',
            color: 'text.primary',
            bgcolor: 'background.default',
            '&:focus': { borderColor: 'primary.main' },
            '&::placeholder': { color: 'text.muted' },
          }}
        />
      </Box>

      <Box
        component="nav"
        aria-label="Filtro por tipo"
        sx={{
          display: 'flex',
          gap: '6px',
          flexWrap: 'wrap',
          alignItems: 'center',
          py: '16px',
          mb: 4,
          borderTop: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        {pills.map((p) => (
          <FilterPill key={p.key} label={`${p.label} · ${p.count}`} active={group === p.key} onClick={() => setGroup(p.key)} />
        ))}
        {sideLinks.length > 0 && (
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {sideLinks.map((l) => (
              <Box key={l.href} component={Link} href={l.href} sx={SECTION_LINK}>
                {l.label}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {filtered.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: { xs: 4, md: 5 },
          }}
        >
          {filtered.map((post) => (
            <PostCard key={post.href} post={post} />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            border: '1px dashed',
            borderColor: 'grey.300',
            borderRadius: 1,
            p: { xs: '32px', md: '48px' },
            textAlign: 'center',
            bgcolor: 'grey.50',
          }}
        >
          <Typography sx={{ fontSize: '15px', color: 'grey.600', mb: 2 }}>{emptyText}</Typography>
          <FilterPill
            label="Limpiar filtros"
            active={false}
            onClick={() => {
              setGroup('todos');
              setQuery('');
            }}
          />
        </Box>
      )}
    </>
  );
}
